import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { Advertiser, CampaignsAdvertiserAdminApiClient } from 'src/app/api/api-campaigns.service.generated';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { BreadcrumbsComponent, ButtonComponent, PageContainerComponent } from 'wecheer-common';
import { AsyncPipe, CommonModule } from '@angular/common';
import { AdvertiserGeneralInfoComponent } from '../advertiser-general-info/advertiser-general-info.component';
import { PERMISSION } from 'src/app/const/permission';
import { AdvertiserOrganizationsComponent } from '../advertiser-organizations/advertiser-organizations.component';
import { AdvertiserLinkedCampaignsComponent } from '../advertiser-linked-campaigns/advertiser-linked-campaigns.component';
import { AdvertiserConsumerDataConnectionComponent } from '../advertiser-consumer-data-connection/advertiser-consumer-data-connection.component';
import { AdvertiserConsumerProfileConfigComponent } from '../advertiser-consumer-profile-config/advertiser-consumer-profile-config.component';
import { OrganizationUsersComponent } from '../advertiser-users/advertiser-users.component';
import { AdvertiserOpenersComponent } from '../advertiser-openers/advertiser-openers.component';
import { AdvertiserOutboundEventsComponent } from '../advertiser-outbound-events/advertiser-outbound-events.component';
import { AdvertiserLoginOptionsComponent } from '../advertiser-login-options/advertiser-login-options.component';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';

@Component({
  selector: 'app-advertiser-settings',
  standalone: true,
  templateUrl: './advertiser-settings.component.html',
  styleUrls: ['./advertiser-settings.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatTabsModule,
    AdvertiserGeneralInfoComponent,
    AdvertiserOrganizationsComponent,
    AdvertiserLinkedCampaignsComponent,
    AdvertiserConsumerDataConnectionComponent,
    AdvertiserConsumerProfileConfigComponent,
    OrganizationUsersComponent,
    AdvertiserOpenersComponent,
    AdvertiserOutboundEventsComponent,
    AdvertiserLoginOptionsComponent,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent,
    AsyncPipe,
  ]
})
export class AdvertiserSettingsComponent implements OnInit, OnDestroy {

  advertiser: Advertiser;
  isLoading = false;
  isBrandAdmin = false;
  selectedTabIndex = 0;

  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);
  private client = inject(CampaignsAdvertiserAdminApiClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private subscriptions: Subscription[] = [];
  private userPermissionsService = inject(UserPermissionsService);

  title = 'Advertiser Settings';

  ngOnInit() {
    this.loadAdvertiser();
    this.checkUserRole();
    this.initializeTabFromRoute();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadAdvertiser() {
    const advertiserId = this.route.snapshot.params['id'];
    if (advertiserId) {
      this.isLoading = true;

      const subscription = this.client.getAdvertiserById(advertiserId).subscribe(
        (advertiser) => {
          this.advertiser = advertiser;
          this.isLoading = false;
          this.title = this.getPageTitle();
        },
        (error) => {
          console.error('Error loading advertiser:', error);
          this.isLoading = false;
        }
      );

      this.subscriptions.push(subscription);
    }
  }

  private checkUserRole() {
    this.userPermissionsService.hasPermission(PERMISSION.BRANDADMIN).subscribe(isBrandAdmin => this.isBrandAdmin = isBrandAdmin);
  }

  onBack() {
    // Navigate back to advertiser details page
    this.router.navigate(['/advertisers', this.advertiser?.id]);
  }

  onRefresh() {
    this.loadAdvertiser();
  }

  getPageTitle(): string {
    if (this.advertiser && this.advertiser.name) {
      return `${this.advertiser.name} Advertiser Settings`;
    }
    return 'Advertiser Settings';
  }

  private initializeTabFromRoute() {
    // Get tab from query param or fragment
    const tabParam = this.route.snapshot.queryParams['tab'] || this.route.snapshot.fragment;
    if (tabParam) {
      const availableTabs = this.getAvailableTabs();
      const tabIndex = availableTabs.indexOf(tabParam);
      if (tabIndex >= 0) {
        this.selectedTabIndex = tabIndex;
      }
    }
  }

  private getAvailableTabs(): string[] {
    const baseTabs = ['general', 'organizations', 'campaigns', 'user-profile'];
    if (!this.isBrandAdmin) {
      baseTabs.push('data-connection', 'users', 'openers', 'outbound-events', 'login-options');
    }
    return baseTabs;
  }

  onTabChange(event: MatTabChangeEvent) {
    const availableTabs = this.getAvailableTabs();
    const tabName = availableTabs[event.index];
    if (tabName) {
      // Update URL with tab parameter
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabName },
        queryParamsHandling: 'merge'
      });
    }
  }

}
